import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { STORAGE_KEYS, type UserRole } from '@/constants';
import type { BackendUser } from '@/types/user';

export interface User {
  id: string | number;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  name?: string;
  role: UserRole;
  balance?: number;
}

export const toStoreUser = (backendUser: BackendUser): User => ({
  id: backendUser.id,
  email: backendUser.email,
  firstName: backendUser.firstName,
  lastName: backendUser.lastName,
  role: backendUser.role,
  balance: backendUser.balance,
});

interface UserState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isHydrated: boolean;
}

interface UserActions {
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  clearUser: () => void;
  setHydrated: (hydrated: boolean) => void;
}

type UserStore = UserState & UserActions;

export const useUserStore = create<UserStore>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isLoading: false,
      isHydrated: false,

      setUser: (user) => set({ user }),
      setToken: (token) => set({ token }),
      setLoading: (isLoading) => set({ isLoading }),
      setHydrated: (isHydrated) => set({ isHydrated }),
      clearUser: () => set({ user: null, token: null }),
    }),
    {
      name: STORAGE_KEYS.AUTH_TOKEN,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated(true);
      },
    }
  )
);

export const selectIsAuthenticated = (state: UserStore): boolean => !!state.user;

export const selectUserRole = (state: UserStore): UserRole | null =>
  state.user?.role || null;

export const selectHasRole = (role: UserRole) => (state: UserStore): boolean =>
  state.user?.role === role;

export const selectHasAnyRole =
  (roles: UserRole[]) =>
  (state: UserStore): boolean =>
    !!state.user?.role && roles.includes(state.user.role);

export const selectUserDisplayName = (state: UserStore): string => {
  if (!state.user) return '';

  if (state.user.name) return state.user.name;

  const firstName = state.user.firstName || '';
  const lastName = state.user.lastName || '';

  if (firstName || lastName) {
    return `${firstName} ${lastName}`.trim();
  }

  return state.user.email;
};

export default useUserStore;
